import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  Text,
  VStack,
  Box,
  useToast,
} from '@chakra-ui/react';
import { LockIcon } from '@chakra-ui/icons';
import API from '../../helpers/axiosInstance';


export function MFAVerificationModal({ isOpen, onClose, mfaToken, onVerificationSuccess }) {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const toast = useToast();

  const getApiHeaders = () => ({
    "x-api-key": localStorage.getItem("api-key"),
    "x-mfa-token": mfaToken,
  });

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  const handleVerifyCode = async () => {
    setIsVerifying(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer-portal/mfa/verify`;
      const response = await API.post(
        url, 
        { 
          mfa_token: mfaToken,
          code: verificationCode 
        }, 
        { headers: getApiHeaders() }
      );

      const data = response.data;
      
      // Store the access token
      localStorage.setItem('customer-portal', data.accessToken);
      // Bug-006c: persist per-user identity for ownership checks (mirrors Login.js).
      localStorage.setItem('user', JSON.stringify({ id: data.id, username: data.username }));
      localStorage.setItem('mfa_enabled', data.mfa_enabled);
      localStorage.setItem('mfa_verified', data.mfa_verified);
      
      // Show success toast
      toast({
        title: "Verification Successful",
        description: "You have been successfully logged in.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      
      // Close modal and call success callback
      onClose();
      if (onVerificationSuccess) {
        onVerificationSuccess();
      }
      
    } catch (error) {
      console.error('Error verifying MFA code:', error);
      
      toast({
        title: "Verification Failed",
        description: error.response?.data?.message || "Invalid code. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsVerifying(false);
    }
  };


  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerifyCode();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Box display="flex" alignItems="center" gap={2}>
            <LockIcon color="var(--brand-primary)" />
            <Text>Two-Factor Authentication</Text>
          </Box>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Enter the 6-digit verification code from your Google Authenticator app.
            </Text>
            
            <Box>
              <Text fontSize="sm" mb={2} fontWeight="medium">
                Verification Code
              </Text>
              <Input
                placeholder="000000"
                value={verificationCode}
                onChange={handleCodeChange}
                onKeyDown={handleKeyDown}
                textAlign="center"
                fontSize="2xl"
                letterSpacing="wider"
                fontFamily="mono"
                maxLength={6}
                size="lg"
                autoFocus
              />
            </Box>

            {/* <Text fontSize="xs" color="gray.500" textAlign="center">
              The code expires in 5 minutes
            </Text> */}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose} isDisabled={isVerifying}>
            Cancel
          </Button>
          <Button
            bg="var(--brand-primary)"
            color="white"
            _hover={{ bg: 'var(--brand-primary-hover)' }}
            onClick={handleVerifyCode}
            isDisabled={verificationCode.length !== 6 || isVerifying}
            isLoading={isVerifying}
            loadingText="Verifying..."
          >
            Verify & Login
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}